import regex from '@/regex';
import {reportError} from '@/Services/Logger';
import {SITE_URL} from '@/constants/urls';
import {getBlogModifiedDate} from '@/util/blogDates';

interface AuthorInfo {
    name?: string;
    image?: string;
    bio?: string;
    linkedin?: string;
    designation?: string;
}

/**
 * Builds the BlogPosting JSON-LD schema for an article page. Pure — safe to run
 * server-side and unit-test. Reusable across any long-form content page (blog,
 * news) so the schema shape stays consistent. Returns null when no post data.
 */
export function buildBlogPostingSchema(
    blogData: any,
    opts: {
        title: string;
        heroImage: string;
        authorInfo?: AuthorInfo;
        basePath?: string;
        schemaType?: string;
        /** Skip the `author` node entirely — for content published with no byline. */
        omitAuthor?: boolean;
    }
) {
    if (!blogData) return null;
    // `basePath`/`schemaType` default to the blog so existing call sites are unchanged.
    // The use-case detail page passes '/use-cases' + 'Article' (a use case is not a blog post).
    const {title, heroImage, authorInfo, basePath = '/blog', schemaType = 'BlogPosting', omitAuthor = false} = opts;
    const url = blogData.yoast_head_json?.og_url ?? `${SITE_URL}${basePath}/${blogData.slug}`;
    return {
        '@context': 'https://schema.org',
        '@type': schemaType,
        headline: title,
        description: blogData.yoast_head_json?.description ?? '',
        image: heroImage,
        url,
        datePublished: blogData.date,
        dateModified: getBlogModifiedDate(blogData),
        mainEntityOfPage: {'@type': 'WebPage', '@id': url},
        ...(omitAuthor || !authorInfo
            ? {}
            : {
                  author: {
                      '@type': 'Person',
                      name: authorInfo.name || 'Debut Infotech',
                      ...(authorInfo.linkedin ? {url: authorInfo.linkedin} : {}),
                      ...(authorInfo.image ? {image: authorInfo.image} : {}),
                      ...(authorInfo.bio ? {description: authorInfo.bio} : {})
                  }
              }),
        publisher: {
            '@type': 'Organization',
            name: 'Debut Infotech',
            url: SITE_URL,
            logo: {'@type': 'ImageObject', url: `${SITE_URL}/images/nav-bar/logo-hover.webp`}
        }
    };
}

/**
 * Builds the BreadcrumbList JSON-LD schema. Pure and reusable. The category
 * crumb is inserted only when a category name is supplied. Returns null when no
 * post data.
 */
export function buildBreadcrumbSchema(
    blogData: any,
    opts: {title: string; blogCategoryName: string; blogCategoryLink: string; basePath?: string; sectionLabel?: string}
) {
    if (!blogData) return null;
    // `basePath`/`sectionLabel` default to the blog so existing call sites are unchanged.
    const {title, blogCategoryName, blogCategoryLink, basePath = '/blog', sectionLabel = 'Blog'} = opts;
    return {
        '@context': 'https://schema.org',
        '@type': 'BreadcrumbList',
        itemListElement: [
            {'@type': 'ListItem', position: 1, name: 'Home', item: SITE_URL},
            {'@type': 'ListItem', position: 2, name: sectionLabel, item: `${SITE_URL}${basePath}`},
            ...(blogCategoryName
                ? [
                      {
                          '@type': 'ListItem',
                          position: 3,
                          name: blogCategoryName,
                          item: `${SITE_URL}${basePath}/category/${blogCategoryLink}`
                      }
                  ]
                : []),
            {
                '@type': 'ListItem',
                position: blogCategoryName ? 4 : 3,
                name: title,
                item: blogData.yoast_head_json?.og_url ?? `${SITE_URL}${basePath}/${blogData.slug}`
            }
        ]
    };
}

/** Strips HTML and normalizes whitespace for JSON-LD plain-text fields (e.g. FAQ `Answer.text`). */
export function stripHtmlForJsonLd(html: string): string {
    return html.replace(regex.STRIP_HTML_TAGS, '').replace(regex.NEWLINE, ' ').replace(regex.COLLAPSE_WHITESPACE, ' ').trim();
}

/** Utility: fix all subdomain references in schema */
export function fixSchemaDomain(schemaObj: any) {
    if (!schemaObj) return null;
    try {
        const str = JSON.stringify(schemaObj);
        let fixed = str.replaceAll('blogs.debutinfotech.com/', 'www.debutinfotech.com/blog/');
        fixed = fixed.replaceAll('www.debutinfotech.com/blog/#', 'www.debutinfotech.com/#');
        fixed = fixed.replaceAll('blogs.debutinfotech.com', 'www.debutinfotech.com');
        return JSON.parse(fixed);
    } catch (e: any) {
        if (!e?.message?.includes('circular')) {
            reportError(e, {context: 'fixSchemaDomain'});
        }
        return schemaObj;
    }
}

/** Utility: clean up FAQ nodes by removing Q. and A. prefixes */
export function cleanFaqNodes(graph: any[]) {
    if (!graph || !Array.isArray(graph)) return graph;
    return graph?.map((node) => {
        if (node['@type'] === 'Question') {
            return {
                ...node,
                name: node.name?.replace(/^Q\.\s*/i, ''),
                acceptedAnswer: {
                    ...node.acceptedAnswer,
                    text: node.acceptedAnswer?.text?.replace(/<strong>A\.\s*<\/strong>/gi, '')
                }
            };
        }
        return node;
    });
}

/**
 * JSON-LD for a listing page (use-case listing, use-case category): a `CollectionPage` whose
 * `mainEntity` is an `ItemList` of the rendered posts, plus the page's `BreadcrumbList`. Only
 * list what the page actually renders, so the schema matches the visible UI. Paths are
 * site-relative and made absolute here.
 */
export function buildCollectionPageSchema(opts: {
    name: string;
    description: string;
    path: string;
    items: {name: string; path: string}[];
    breadcrumbs: {name: string; path: string}[];
}) {
    const {name, description, path, items, breadcrumbs} = opts;
    const url = `${SITE_URL}${path}`;
    return {
        '@context': 'https://schema.org',
        '@graph': [
            {
                '@type': 'CollectionPage',
                '@id': `${url}#webpage`,
                url,
                name,
                description,
                isPartOf: {'@type': 'WebSite', url: SITE_URL, name: 'Debut Infotech'},
                breadcrumb: {'@id': `${url}#breadcrumb`},
                mainEntity: {
                    '@type': 'ItemList',
                    numberOfItems: items.length,
                    itemListElement: items.map((item, index) => ({
                        '@type': 'ListItem',
                        position: index + 1,
                        name: item.name,
                        url: `${SITE_URL}${item.path}`
                    }))
                }
            },
            {
                '@type': 'BreadcrumbList',
                '@id': `${url}#breadcrumb`,
                itemListElement: breadcrumbs.map((crumb, index) => ({
                    '@type': 'ListItem',
                    position: index + 1,
                    name: crumb.name,
                    item: `${SITE_URL}${crumb.path}`
                }))
            }
        ]
    };
}
